"use client";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "components/Reusable/AuthProvider";
import { useSettings } from "components/Reusable/SettingsProvider";
import { useTheme } from "components/Reusable/ThemeProvider";
import {
  getCategoryLabelKey,
  getCountryLabelKey,
  getTypeLabelKey,
  LEGAL_CATEGORIES,
  LEGAL_COUNTRIES,
  LEGAL_TYPES,
} from "../../../../models/legalDomainConstants";
import {
  Bell,
  CreditCard,
  FileText,
  Globe,
  Mail,
  Menu,
  MessageSquare,
  Monitor,
  Moon,
  Shield,
  Sun,
  Users,
  X,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  AlertNotificationHistory,
  CustomAlert,
} from "../../../../models/customAlert";
import { User } from "../../../../models/user";
import { AxiosHttpClient } from "../../../../services/AxiosHttpService";
import { CustomAlertService } from "../../../../services/CustomAlertService";
import Pagination from "../../../Reusable/Pagination";
import { useDateFormatting } from "../../../Reusable/Utils/useDateFormatting";
import DataExportManager from "./DataExportManager";

const settingsTabs = [
  { id: "preferences", label: "Préférences", icon: Globe },
  { id: "chatbot", label: "Chatbot", icon: MessageSquare },
  { id: "alerts", label: "Alertes", icon: Bell },
  { id: "privacy", label: "Confidentialité", icon: Shield },
  { id: "subscription", label: "Abonnement", icon: CreditCard },
  { id: "members", label: "Membres", icon: Users },
];

export default function SettingsPage() {
  const [searchParams] = useSearchParams();
  const initialTabParam = searchParams.get("tab");
  const [activeTab, setActiveTab] = useState(
    initialTabParam && settingsTabs.some((tab) => tab.id === initialTabParam)
      ? initialTabParam
      : "preferences"
  );
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false); // Nouveau état pour tracker l'initialisation

  const {
    settings,
    loading: settingsLoading,
    error,
    updateSettings,
  } = useSettings();

  const { theme, setTheme } = useTheme();
  const { user: userConnected } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { formatDateTime } = useDateFormatting();

  // Synchroniser l'onglet actif avec le paramètre d'URL (ex: /feature/settings?tab=alerts)
  useEffect(() => {
    const tabParam = searchParams.get("tab");
    if (tabParam && settingsTabs.some((tab) => tab.id === tabParam)) {
      setActiveTab(tabParam);
    }
  }, [searchParams]);

  // États locaux pour les modifications (initialisés depuis le contexte)
  const [language, setLanguage] = useState("fr");
  const [timezone, setTimezone] = useState("europe/paris");
  const [dateFormat, setDateFormat] = useState("DD/MM/YYYY");
  const [chatbotLanguage, setChatbotLanguage] = useState("fr");
  const [responseDetailLevel, setResponseDetailLevel] = useState("standard");
  const [showSources, setShowSources] = useState(true);
  const [explainConfidence, setExplainConfidence] = useState(false);
  const [conversationStorageDuration, setConversationStorageDuration] =
    useState("30days");
  const [allowTraining, setAllowTraining] = useState(false);
  const [alertsEnabled, setAlertsEnabled] = useState(true);
  const [alertFrequency, setAlertFrequency] = useState("daily");
  const [companyName, setCompanyName] = useState("");
  const [address, setAddress] = useState("");
  const [vatNumber, setVatNumber] = useState("");

  // États pour la modification du mot de passe
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState<{
    currentPassword?: string;
    newPassword?: string;
    confirmNewPassword?: string;
  }>({});

  // États pour la popup d'invitation
  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteFirstName, setInviteFirstName] = useState("");
  const [inviteLastName, setInviteLastName] = useState("");
  const [inviteRole, setInviteRole] = useState("MEMBER");
  const [isInviting, setIsInviting] = useState(false);

  // États pour la gestion des membres
  const [members, setMembers] = useState<User[]>([]);
  const [isLoadingMembers, setIsLoadingMembers] = useState(false);
  const [membersPage, setMembersPage] = useState(1);
  const [totalMembers, setTotalMembers] = useState(0);
  const [resendingInvitationIds, setResendingInvitationIds] = useState<
    string[]
  >([]);
  const [revokingUserIds, setRevokingUserIds] = useState<string[]>([]);

  // États pour la popup de modification de rôle
  const [isRoleDialogOpen, setIsRoleDialogOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState<User | null>(null);
  const [newRole, setNewRole] = useState("");
  const [isUpdatingRole, setIsUpdatingRole] = useState(false);

  // États pour la popup de confirmation de révocation
  const [isRevokeDialogOpen, setIsRevokeDialogOpen] = useState(false);
  const [memberToRevoke, setMemberToRevoke] = useState<User | null>(null);

  // États pour la gestion des alertes personnalisées
  const [customAlerts, setCustomAlerts] = useState<CustomAlert[]>([]);
  const [isLoadingAlerts, setIsLoadingAlerts] = useState(false);
  const [alertsPage, setAlertsPage] = useState(1);
  const [totalAlerts, setTotalAlerts] = useState(0);
  const [isAlertDialogOpen, setIsAlertDialogOpen] = useState(false);
  const [editingAlert, setEditingAlert] = useState<CustomAlert | null>(null);
  const [isCreatingAlert, setIsCreatingAlert] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [alertToDelete, setAlertToDelete] = useState<string | null>(null);

  // États pour le formulaire d'alerte
  const [alertName, setAlertName] = useState("");
  const [alertKeywords, setAlertKeywords] = useState("");
  const [alertSemanticSearch, setAlertSemanticSearch] = useState("");
  const [alertJurisdictions, setAlertJurisdictions] = useState<string[]>([]);
  const [alertCategories, setAlertCategories] = useState<string[]>([]);
  const [alertDocumentTypes, setAlertDocumentTypes] = useState<string[]>([]);
  const [alertSimilarityScore, setAlertSimilarityScore] = useState<number>(85);
  const [alertIsActive, setAlertIsActive] = useState(true);

  // États pour les canaux de réception (séparés)
  const [alertEmail, setAlertEmail] = useState(true);
  const [alertInApp, setAlertInApp] = useState(true);

  // États pour l'historique des notifications d'alertes
  const [isHistoryDialogOpen, setIsHistoryDialogOpen] = useState(false);
  const [notificationHistory, setNotificationHistory] = useState<
    AlertNotificationHistory[]
  >([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const [totalHistory, setTotalHistory] = useState(0);

  const membersPageSize = 5;
  const alertsPageSize = 5;
  const historyPageSize = 5;

  // Listes de catégories et types depuis LegalDocuments
  const categories = LEGAL_CATEGORIES;
  const documentTypes = LEGAL_TYPES;
  const jurisdictions = LEGAL_COUNTRIES.map((c) => c.value);

  const { toast } = useToast();

  // Fonction pour récupérer les membres de l'entité
  const getMembersPage = (pageNumber: number = membersPage) => {
    setIsLoadingMembers(true);
    console.log("userConnected", userConnected);
    AxiosHttpClient.get<any>("api/v1/docuveritas/users/page", {
      criteriaList: "entity_id=" + userConnected?.entity.id, // Filtrer
      pageNumber: pageNumber - 1,
      pageSize: membersPageSize,
      sort: "lastName",
      order: "asc",
    })
      .then((res) => {
        setMembers(res.content);
        setTotalMembers(res.totalElements);
      })
      .catch((error) => {
        console.error("Erreur lors du chargement des membres:", error);
        toast({
          title: "Erreur",
          description: "Impossible de charger la liste des membres",
          variant: "destructive",
        });
      })
      .finally(() => {
        setIsLoadingMembers(false);
      });
  };

  // Synchroniser les états locaux avec les settings du contexte
  useEffect(() => {
    console.log("Settings chargés dans le composant:", settings);
    if (settings) {
      console.log("langue:", settings.language);
      setLanguage(settings.language);
      setTimezone(settings.timezone);
      setDateFormat(settings.dateFormat);
      setChatbotLanguage(settings.chatbotLanguage);
      setResponseDetailLevel(settings.responseDetailLevel);
      setShowSources(settings.showSources === "true");
      setExplainConfidence(settings.explainConfidence === "true");
      setConversationStorageDuration(settings.conversationStorageDuration);
      setAllowTraining(settings.allowTraining === "true");
      setAlertsEnabled(settings.alertsEnabled === "true");
      // Charger les canaux de notification séparés
      if (settings.alertEmail !== undefined) {
        setAlertEmail(settings.alertEmail === "true");
      }
      if (settings.alertInApp !== undefined) {
        setAlertInApp(settings.alertInApp === "true");
      }
      setAlertFrequency(settings.alertFrequency);
      setCompanyName(settings.companyName);
      setAddress(settings.address);
      setVatNumber(settings.vatNumber);

      // Marquer comme initialisé après avoir mis à jour tous les états
      setIsInitialized(true);
    }
  }, [settings]);

  // Charger les membres au montage du composant
  useEffect(() => {
    getMembersPage();
  }, [userConnected]);

  // Recharger les membres quand la page change
  useEffect(() => {
    getMembersPage(membersPage);
  }, [membersPage]);

  // Fonction pour sauvegarder automatiquement un paramètre
  const autoSaveSettings = async (key: string, value: any) => {
    try {
      console.log(`Sauvegarde automatique: ${key} = ${value}`);
      await updateSettings({ [key]: value });
      console.log(`Paramètre ${key} sauvegardé avec succès`);
    } catch (error) {
      console.error(`Erreur lors de la sauvegarde de ${key}:`, error);
      // Optionnel: Afficher une notification d'erreur discrète
    }
  };

  // Handlers avec sauvegarde automatique
  const handleLanguageChange = (value: string) => {
    setLanguage(value);
    autoSaveSettings("language", value);
  };

  const handleTimezoneChange = (value: string) => {
    setTimezone(value);
    autoSaveSettings("timezone", value);
  };

  const handleDateFormatChange = (value: string) => {
    setDateFormat(value);
    autoSaveSettings("dateFormat", value);
  };

  const handleChatbotLanguageChange = (value: string) => {
    setChatbotLanguage(value);
    autoSaveSettings("chatbotLanguage", value);
  };

  const handleResponseDetailLevelChange = (value: string) => {
    setResponseDetailLevel(value);
    autoSaveSettings("responseDetailLevel", value);
  };

  const handleShowSourcesChange = (checked: boolean) => {
    setShowSources(checked);
    autoSaveSettings("showSources", checked.toString());
  };

  const handleExplainConfidenceChange = (checked: boolean) => {
    setExplainConfidence(checked);
    autoSaveSettings("explainConfidence", checked.toString());
  };

  const handleConversationStorageDurationChange = (value: string) => {
    setConversationStorageDuration(value);
    autoSaveSettings("conversationStorageDuration", value);
  };

  const handleAllowTrainingChange = (checked: boolean) => {
    setAllowTraining(checked);
    autoSaveSettings("allowTraining", checked.toString());
  };

  const handleAlertsEnabledChange = (checked: boolean) => {
    setAlertsEnabled(checked);
    autoSaveSettings("alertsEnabled", checked.toString());
  };

  const handleNotificationChannelChange = (
    channel: "email" | "inApp",
    checked: boolean
  ) => {
    if (channel === "email") {
      setAlertEmail(checked);
      autoSaveSettings("alertEmail", checked.toString());
    } else if (channel === "inApp") {
      setAlertInApp(checked);
      autoSaveSettings("alertInApp", checked.toString());
    }
  };

  const handleAlertFrequencyChange = (value: string) => {
    setAlertFrequency(value);
    autoSaveSettings("alertFrequency", value);
  };

  const handleCompanyNameChange = (value: string) => {
    setCompanyName(value);
    autoSaveSettings("companyName", value);
  };

  const handleAddressChange = (value: string) => {
    setAddress(value);
    autoSaveSettings("address", value);
  };

  const handleVatNumberChange = (value: string) => {
    setVatNumber(value);
    autoSaveSettings("vatNumber", value);
  };

  // Fonction pour valider le mot de passe
  const validatePassword = (password: string): boolean => {
    const minLength = password.length >= 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);

    return minLength && hasUpperCase && hasLowerCase && hasNumber;
  };

  // Fonction pour réinitialiser les erreurs de mot de passe
  const clearPasswordErrors = () => {
    setPasswordErrors({});
  };

  // Fonction pour changer le mot de passe
  const handleChangePassword = async () => {
    // Réinitialiser les erreurs
    clearPasswordErrors();
    const errors: typeof passwordErrors = {};

    // Validation des champs
    if (!currentPassword.trim()) {
      errors.currentPassword = "L'ancien mot de passe est requis";
    }

    if (!newPassword.trim()) {
      errors.newPassword = "Le nouveau mot de passe est requis";
    } else if (!validatePassword(newPassword)) {
      errors.newPassword =
        "Le mot de passe doit contenir au moins 8 caractères, une majuscule, une minuscule et un chiffre";
    }

    if (!confirmNewPassword.trim()) {
      errors.confirmNewPassword = "La confirmation du mot de passe est requise";
    } else if (newPassword !== confirmNewPassword) {
      errors.confirmNewPassword = "Les mots de passe ne correspondent pas";
    }

    if (Object.keys(errors).length > 0) {
      setPasswordErrors(errors);
      return;
    }

    setIsChangingPassword(true);

    try {
      await AxiosHttpClient.put("api/v1/docuveritas/account/changePassword", {
        oldPassword: currentPassword,
        newPassword: newPassword,
      });

      toast({
        title: "Succès",
        description: "Votre mot de passe a été modifié avec succès",
        variant: "success",
      });

      // Réinitialiser les champs
      setCurrentPassword("");
      setNewPassword("");
      setConfirmNewPassword("");
    } catch (error: any) {
      console.error("Erreur lors du changement de mot de passe:", error);

      let errorMessage =
        "Une erreur est survenue lors du changement de mot de passe";

      if (error.cause) {
        errorMessage = error.cause;
      } else if (error.type === "BAD_REQUEST") {
        errorMessage = "Ancien mot de passe incorrect";
      } else if (error.type === "FORBIDDEN") {
        errorMessage = "Vous n'êtes pas autorisé à effectuer cette action";
      }

      toast({
        title: "Erreur",
        description: errorMessage,
        variant: "destructive",
      });
      console.log("errorMessage: " + errorMessage);
    } finally {
      setIsChangingPassword(false);
    }
  };

  // Fonction pour inviter un membre
  const handleInviteMember = async () => {
    // Validation des champs
    if (
      !inviteEmail.trim() ||
      !inviteFirstName.trim() ||
      !inviteLastName.trim()
    ) {
      toast({
        title: "Erreur",
        description: "Veuillez remplir tous les champs obligatoires",
        variant: "destructive",
      });
      return;
    }

    // Validation de l'email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(inviteEmail)) {
      toast({
        title: "Erreur",
        description: "Veuillez saisir une adresse email valide",
        variant: "destructive",
      });
      return;
    }

    setIsInviting(true);

    try {
      await AxiosHttpClient.post("api/v1/docuveritas/users/invite", {
        email: inviteEmail,
        firstName: inviteFirstName,
        lastName: inviteLastName,
        role: inviteRole,
      });

      toast({
        title: "Succès",
        description: `Invitation envoyée avec succès à ${inviteEmail}`,
        variant: "success",
      });

      // Réinitialiser le formulaire et fermer la popup
      setInviteEmail("");
      setInviteFirstName("");
      setInviteLastName("");
      setInviteRole("MEMBER");
      setIsInviteDialogOpen(false);

      // Rafraîchir la liste des membres (retourner à la page 1)
      setMembersPage(1);
      getMembersPage(1);
    } catch (error: any) {
      console.error("Erreur lors de l'invitation:", error);

      let errorMessage =
        "Une erreur est survenue lors de l'envoi de l'invitation";

      if (error.cause) {
        errorMessage = error.cause;
      } else if (error.type === "BAD_REQUEST") {
        errorMessage = "Les données fournies sont invalides";
      } else if (error.type === "CONFLICT") {
        errorMessage = "Cette personne est déjà membre de votre équipe";
      } else if (error.type === "FORBIDDEN") {
        errorMessage = "Vous n'êtes pas autorisé à inviter des membres";
      }

      toast({
        title: "Erreur",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsInviting(false);
    }
  };

  // Fonction pour renvoyer une invitation
  const handleResendInvitation = async (userId: string, userEmail: string) => {
    setResendingInvitationIds((prev) => [...prev, userId]);

    try {
      await AxiosHttpClient.post(
        `api/v1/docuveritas/users/resend-invitation/${userId}`
      );

      toast({
        title: "Invitation renvoyée",
        description: `Invitation renvoyée avec succès à ${userEmail}`,
        variant: "success",
      });

      // Rafraîchir la liste des membres
      getMembersPage(membersPage);
    } catch (error: any) {
      console.error("Erreur lors du renvoi de l'invitation:", error);

      let errorMessage =
        "Une erreur est survenue lors du renvoi de l'invitation";

      if (error.cause) {
        errorMessage = error.cause;
      } else if (error.type === "BAD_REQUEST") {
        errorMessage =
          "Utilisateur non trouvé ou non éligible pour le renvoi d'invitation";
      } else if (error.type === "INTERNAL_SERVER_ERROR") {
        errorMessage = "Erreur lors du renvoi de l'invitation";
      }

      toast({
        title: "Erreur",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setResendingInvitationIds((prev) => prev.filter((id) => id !== userId));
    }
  };

  // Fonction pour ouvrir la popup de confirmation de révocation
  const handleOpenRevokeDialog = (member: User) => {
    setMemberToRevoke(member);
    setIsRevokeDialogOpen(true);
  };

  // Fonction pour révoquer l'accès d'un utilisateur
  const handleRevokeUser = async () => {
    if (!memberToRevoke) return;

    setRevokingUserIds((prev) => [...prev, memberToRevoke.id]);

    try {
      // TODO: Remplacer par l'endpoint réel de révocation quand il sera disponible
      await AxiosHttpClient.delete(
        `api/v1/docuveritas/users/revoke/${memberToRevoke.id}`
      );

      toast({
        title: "Accès révoqué",
        description: `L'accès de ${memberToRevoke.email} a été révoqué avec succès`,
        variant: "success",
      });

      // Fermer la popup
      setIsRevokeDialogOpen(false);
      setMemberToRevoke(null);

      // Rafraîchir la liste des membres
      getMembersPage(membersPage);
    } catch (error: any) {
      console.error("Erreur lors de la révocation:", error);

      let errorMessage =
        "Une erreur est survenue lors de la révocation de l'accès";

      if (error.cause) {
        errorMessage = error.cause;
      } else if (error.type === "NOT_FOUND") {
        errorMessage = "Utilisateur non trouvé";
      } else if (error.type === "BAD_REQUEST") {
        errorMessage = "Utilisateur non éligible pour la révocation";
      } else if (error.type === "FORBIDDEN") {
        errorMessage = "Vous n'êtes pas autorisé à révoquer cet utilisateur";
      } else if (error.type === "INTERNAL_SERVER_ERROR") {
        errorMessage = "Erreur lors de la révocation de l'accès utilisateur";
      }

      toast({
        title: "Erreur",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setRevokingUserIds((prev) =>
        prev.filter((id) => id !== memberToRevoke.id)
      );
    }
  };

  // Fonction pour ouvrir la popup de modification de rôle
  const handleOpenRoleDialog = (member: User) => {
    setSelectedMember(member);
    // Déterminer le rôle actuel
    let currentRole = "MEMBER";
    if (member.roles.includes("MANAGER")) {
      currentRole = "MANAGER";
    }
    setNewRole(currentRole);
    setIsRoleDialogOpen(true);
  };

  // Fonction pour modifier le rôle d'un utilisateur
  const handleUpdateMemberRole = async () => {
    if (!selectedMember) return;

    setIsUpdatingRole(true);

    try {
      await AxiosHttpClient.put(
        `api/v1/docuveritas/users/role/${selectedMember.id}`,
        {
          role: newRole,
        }
      );

      toast({
        title: "Rôle modifié",
        description: `Le rôle de ${selectedMember.firstName} ${selectedMember.lastName} a été modifié avec succès`,
        variant: "success",
      });

      // Fermer la popup
      setIsRoleDialogOpen(false);
      setSelectedMember(null);

      // Rafraîchir la liste des membres
      getMembersPage(membersPage);
    } catch (error: any) {
      console.error("Erreur lors de la modification du rôle:", error);

      let errorMessage =
        "Une erreur est survenue lors de la modification du rôle";

      if (error.cause) {
        errorMessage = error.cause;
      } else if (error.type === "NOT_FOUND") {
        errorMessage = "Utilisateur non trouvé";
      } else if (error.type === "BAD_REQUEST") {
        errorMessage = "Rôle invalide ou données incorretes";
      } else if (error.type === "FORBIDDEN") {
        errorMessage =
          "Vous n'êtes pas autorisé à modifier le rôle de cet utilisateur";
      } else if (error.type === "INTERNAL_SERVER_ERROR") {
        errorMessage = "Erreur lors de la modification du rôle";
      }

      toast({
        title: "Erreur",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsUpdatingRole(false);
    }
  };

  // ========== GESTION DES ALERTES PERSONNALISÉES ==========

  // Fonction pour récupérer les alertes personnalisées
  const getCustomAlertsPage = (pageNumber: number = alertsPage) => {
    setIsLoadingAlerts(true);
    CustomAlertService.getCustomAlerts(pageNumber, alertsPageSize)
      .then((res) => {
        setCustomAlerts(res.content);
        setTotalAlerts(res.totalElements);
      })
      .catch((error) => {
        console.error("Erreur lors du chargement des alertes:", error);
        toast({
          title: "Erreur",
          description: "Impossible de charger la liste des alertes",
          variant: "destructive",
        });
      })
      .finally(() => {
        setIsLoadingAlerts(false);
      });
  };

  // Charger les alertes au montage si on est sur l'onglet alertes
  useEffect(() => {
    if (activeTab === "alerts") {
      getCustomAlertsPage();
    }
  }, [activeTab]);

  // Recharger les alertes quand la page change
  useEffect(() => {
    if (activeTab === "alerts") {
      getCustomAlertsPage(alertsPage);
    }
  }, [alertsPage]);

  // Réinitialiser le formulaire d'alerte
  const resetAlertForm = () => {
    setAlertName("");
    setAlertKeywords("");
    setAlertSemanticSearch("");
    setAlertJurisdictions([]);
    setAlertCategories([]);
    setAlertDocumentTypes([]);
    setAlertSimilarityScore(85);
    setAlertIsActive(true);
    setEditingAlert(null);
  };

  // Ouvrir le dialogue pour créer une nouvelle alerte
  const handleOpenCreateAlertDialog = () => {
    resetAlertForm();
    setIsAlertDialogOpen(true);
  };

  // Ouvrir le dialogue pour modifier une alerte
  const handleOpenEditAlertDialog = (alert: CustomAlert) => {
    setEditingAlert(alert);
    setAlertName(alert.name);
    setAlertKeywords(alert.keywords || "");
    setAlertSemanticSearch(alert.semanticSearch || "");
    setAlertJurisdictions(alert.jurisdictions || []);
    setAlertCategories(alert.categories || []);
    setAlertDocumentTypes(alert.documentTypes || []);
    setAlertSimilarityScore(alert.similarityScore || 85);
    setAlertIsActive(alert.isActive);
    setIsAlertDialogOpen(true);
  };

  // Sauvegarder une alerte (créer ou modifier)
  const handleSaveAlert = async () => {
    // Validation
    if (!alertKeywords.trim() && !alertSemanticSearch.trim()) {
      toast({
        title: "Erreur",
        description:
          "Vous devez définir soit des mots-clés, soit une recherche sémantique",
        variant: "destructive",
      });
      return;
    }

    setIsCreatingAlert(true);

    try {
      // Générer un nom automatique si non fourni
      let finalAlertName = alertName.trim();
      if (!finalAlertName) {
        const parts: string[] = [];

        // Ajouter les mots-clés au nom
        if (alertKeywords.trim()) {
          const keywordsArray = alertKeywords.split(",").map((k) => k.trim());
          const keywordPart =
            keywordsArray.length === 1
              ? keywordsArray[0]
              : `${keywordsArray[0]} +${keywordsArray.length - 1}`;
          parts.push(keywordPart);
        }

        // Ajouter la recherche sémantique
        if (alertSemanticSearch.trim()) {
          const truncatedSearch = alertSemanticSearch.trim().substring(0, 30);
          parts.push(
            `${truncatedSearch}${alertSemanticSearch.length > 30 ? "..." : ""}`
          );
        }

        // Ajouter les catégories au nom
        if (alertCategories.length > 0) {
          const categoryPart =
            alertCategories.length === 1
              ? alertCategories[0]
              : `${alertCategories[0]} +${alertCategories.length - 1}`;
          parts.push(categoryPart);
        }

        // Ajouter les types de documents au nom
        if (alertDocumentTypes.length > 0) {
          const typePart =
            alertDocumentTypes.length === 1
              ? alertDocumentTypes[0]
              : `${alertDocumentTypes[0]} +${alertDocumentTypes.length - 1}`;
          parts.push(typePart);
        }

        // Ajouter les juridictions au nom (à la fin)
        if (alertJurisdictions.length > 0) {
          const jurisdictionPart =
            alertJurisdictions.length === 1
              ? alertJurisdictions[0]
              : `${alertJurisdictions[0]} +${alertJurisdictions.length - 1}`;
          parts.push(jurisdictionPart);
        }

        // Créer le nom final
        if (parts.length > 0) {
          finalAlertName = `Alerte ${parts.join(" - ")}`;
        } else {
          // Fallback si aucun critère n'est défini (ne devrait pas arriver)
          finalAlertName = `Alerte ${new Date().toLocaleDateString("fr-FR")}`;
        }
      }

      const alertData = {
        name: finalAlertName,
        keywords: alertKeywords.trim() || undefined,
        semanticSearch: alertSemanticSearch.trim() || undefined,
        jurisdictions:
          alertJurisdictions.length > 0 ? alertJurisdictions : undefined,
        categories: alertCategories.length > 0 ? alertCategories : undefined,
        documentTypes:
          alertDocumentTypes.length > 0 ? alertDocumentTypes : undefined,
        similarityScore: alertSemanticSearch ? alertSimilarityScore : undefined,
        isActive: alertIsActive,
      };

      if (editingAlert) {
        // Modification
        await CustomAlertService.updateCustomAlert(editingAlert.id, alertData);
        toast({
          title: "Succès",
          description: "L'alerte a été modifiée avec succès",
          variant: "success",
        });
      } else {
        // Création
        await CustomAlertService.createCustomAlert(alertData);
        toast({
          title: "Succès",
          description: "L'alerte a été créée avec succès",
          variant: "success",
        });
      }

      // Fermer le dialogue et rafraîchir la liste
      setIsAlertDialogOpen(false);
      resetAlertForm();
      getCustomAlertsPage(alertsPage);
    } catch (error: any) {
      console.error("Erreur lors de la sauvegarde de l'alerte:", error);
      toast({
        title: "Erreur",
        description:
          error.cause ||
          "Une erreur est survenue lors de la sauvegarde de l'alerte",
        variant: "destructive",
      });
    } finally {
      setIsCreatingAlert(false);
    }
  };

  // Supprimer une alerte
  // Ouvrir le dialogue de confirmation de suppression
  const handleOpenDeleteDialog = (alertId: string) => {
    setAlertToDelete(alertId);
    setIsDeleteDialogOpen(true);
  };

  // Confirmer la suppression de l'alerte
  const handleConfirmDeleteAlert = async () => {
    if (!alertToDelete) return;

    try {
      await CustomAlertService.deleteCustomAlert(alertToDelete);
      toast({
        title: "Succès",
        description: "L'alerte a été supprimée avec succès",
        variant: "success",
      });
      setIsDeleteDialogOpen(false);
      setAlertToDelete(null);
      getCustomAlertsPage(alertsPage);
    } catch (error: any) {
      console.error("Erreur lors de la suppression de l'alerte:", error);
      toast({
        title: "Erreur",
        description:
          error.cause ||
          "Une erreur est survenue lors de la suppression de l'alerte",
        variant: "destructive",
      });
    }
  };

  // Annuler la suppression
  const handleCancelDelete = () => {
    setIsDeleteDialogOpen(false);
    setAlertToDelete(null);
  };

  // Activer/Désactiver une alerte
  const handleToggleAlert = async (alertId: string, isActive: boolean) => {
    try {
      await CustomAlertService.toggleCustomAlert(alertId, isActive);

      // Mettre à jour localement
      setCustomAlerts((prev) =>
        prev.map((alert) =>
          alert.id === alertId ? { ...alert, isActive } : alert
        )
      );

      toast({
        title: "Succès",
        description: `L'alerte a été ${isActive ? "activée" : "désactivée"}`,
        variant: "success",
      });
    } catch (error: any) {
      console.error("Erreur lors du changement de statut de l'alerte:", error);
      toast({
        title: "Erreur",
        description: error.cause || "Une erreur est survenue",
        variant: "destructive",
      });
      // Recharger pour restaurer l'état correct
      getCustomAlertsPage(alertsPage);
    }
  };

  // ========== FIN GESTION DES ALERTES PERSONNALISÉES ==========

  // ========== HISTORIQUE DES NOTIFICATIONS D'ALERTES ==========

  const notificationTypeLabels: Record<string, string> = {
    IMMEDIATE: "Immédiat",
    DAILY: "Quotidien",
    WEEKLY: "Hebdomadaire",
    MONTHLY: "Mensuel",
  };

  // Récupérer une page de l'historique des notifications
  const getNotificationHistoryPage = (pageNumber: number = historyPage) => {
    setIsLoadingHistory(true);
    CustomAlertService.getNotificationHistory(pageNumber, historyPageSize)
      .then((res) => {
        setNotificationHistory(res.content);
        setTotalHistory(res.totalElements);
      })
      .catch((error) => {
        console.error("Erreur lors du chargement de l'historique:", error);
        toast({
          title: "Erreur",
          description:
            "Impossible de charger l'historique des notifications",
          variant: "destructive",
        });
      })
      .finally(() => {
        setIsLoadingHistory(false);
      });
  };

  // Ouvrir le dialogue d'historique (repart toujours de la première page)
  const handleOpenHistoryDialog = () => {
    setHistoryPage(1);
    setIsHistoryDialogOpen(true);
  };

  // Charger l'historique quand le dialogue est ouvert ou que la page change
  useEffect(() => {
    if (isHistoryDialogOpen) {
      getNotificationHistoryPage(historyPage);
    }
  }, [isHistoryDialogOpen, historyPage]);

  // Naviguer vers un document depuis l'historique
  const handleOpenHistoryDocument = (documentId: string) => {
    setIsHistoryDialogOpen(false);
    navigate(`/feature/legalDocuments/pdf-viewer/${documentId}`);
  };

  // ========== FIN HISTORIQUE DES NOTIFICATIONS D'ALERTES ==========

  // Afficher un loader pendant le chargement initial
  if (settingsLoading || !settings || !isInitialized) {
    return (
      <div className="h-full w-full bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="text-lg font-medium">
            Chargement des paramètres...
          </div>
          <div className="text-sm text-muted-foreground mt-2">
            Veuillez patienter pendant que nous récupérons vos préférences
          </div>
        </div>
      </div>
    );
  }

  // Afficher une erreur si le chargement a échoué
  if (error) {
    return (
      <div className="h-full w-full bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="text-lg font-medium text-red-600">Erreur</div>
          <div className="text-sm text-muted-foreground">{error}</div>
          <Button
            className="mt-4"
            onClick={() => window.location.reload()}
            variant="outline"
          >
            Recharger la page
          </Button>
        </div>
      </div>
    );
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case "preferences":
        return (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Préférences générales</CardTitle>
                <CardDescription>
                  Personnalisez votre expérience utilisateur
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Langue</Label>
                  <Select value={language} onValueChange={handleLanguageChange}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="en">
                        <div className="flex items-center gap-2">
                          <img
                            src="https://flagcdn.com/w40/us.png"
                            alt="English"
                            className="w-6 h-4 rounded"
                          />
                          <span>English</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="fr">
                        <div className="flex items-center gap-2">
                          <img
                            src="https://flagcdn.com/w40/fr.png"
                            alt="French"
                            className="w-6 h-4 rounded"
                          />
                          <span>Français</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="ar">
                        <div className="flex items-center gap-2">
                          <img
                            src="https://flagcdn.com/w40/sa.png"
                            alt="Arabic"
                            className="w-6 h-4 rounded"
                          />
                          <span>العربية</span>
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Fuseau horaire</Label>
                  <Select value={timezone} onValueChange={handleTimezoneChange}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="europe/paris">
                        Europe/Paris (GMT+1)
                      </SelectItem>
                      <SelectItem value="europe/london">
                        Europe/London (GMT+0)
                      </SelectItem>
                      <SelectItem value="america/new_york">
                        America/New_York (GMT-5)
                      </SelectItem>
                      <SelectItem value="asia/tokyo">
                        Asia/Tokyo (GMT+9)
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Thème</Label>
                  <Select value={theme} onValueChange={setTheme}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="light">
                        <div className="flex items-center gap-2">
                          <Sun className="h-4 w-4" />
                          <span>Clair</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="dark">
                        <div className="flex items-center gap-2">
                          <Moon className="h-4 w-4" />
                          <span>Sombre</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="system">
                        <div className="flex items-center gap-2">
                          <Monitor className="h-4 w-4" />
                          <span>Système</span>
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Préférences de format de date/heures</Label>
                  <Select
                    value={dateFormat}
                    onValueChange={handleDateFormatChange}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="DD/MM/YYYY">DD/MM/YYYY</SelectItem>
                      <SelectItem value="MM/DD/YYYY">MM/DD/YYYY</SelectItem>
                      <SelectItem value="YYYY-MM-DD">YYYY-MM-DD</SelectItem>
                      <SelectItem value="DD-MM-YYYY">DD-MM-YYYY</SelectItem>
                      <SelectItem value="MM-DD-YYYY">MM-DD-YYYY</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>
          </div>
        );

      case "chatbot":
        return (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Préférences du chatbot IA</CardTitle>
                <CardDescription>
                  Configurez le comportement du chatbot juridique
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Langue préférée des réponses</Label>
                  <Select
                    value={chatbotLanguage}
                    onValueChange={handleChatbotLanguageChange}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fr">Français</SelectItem>
                      <SelectItem value="en">English</SelectItem>
                      <SelectItem value="es">Español</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Niveau de détail / style de réponse</Label>
                  <Select
                    value={responseDetailLevel}
                    onValueChange={handleResponseDetailLevelChange}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="court">Court</SelectItem>
                      <SelectItem value="standard">Standard</SelectItem>
                      <SelectItem value="detaille">Détaillé</SelectItem>
                      <SelectItem value="juridique">
                        Juridique (ton expert)
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Separator />
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Sources citées</Label>
                    <p className="text-sm text-muted-foreground">
                      Afficher les sources des réponses
                    </p>
                  </div>
                  <Switch
                    checked={showSources}
                    onCheckedChange={handleShowSourcesChange}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Expliquer la confiance</Label>
                    <p className="text-sm text-muted-foreground">
                      Afficher un seuil de confiance renvoyé par le modèle
                    </p>
                  </div>
                  <Switch
                    checked={explainConfidence}
                    onCheckedChange={handleExplainConfidenceChange}
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Stockage et confidentialité</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Stockage des conversations</Label>
                  <Select
                    value={conversationStorageDuration}
                    onValueChange={handleConversationStorageDurationChange}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="7days">Conserver 7 jours</SelectItem>
                      <SelectItem value="30days">Conserver 30 jours</SelectItem>
                      <SelectItem value="90days">Conserver 90 jours</SelectItem>
                      <SelectItem value="auto">
                        Supprimer automatiquement
                      </SelectItem>
                      <SelectItem value="never">Ne jamais stocker</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>
                      Autoriser l'utilisation des conversations pour entraîner
                      les modèles internes
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      Opt-in pour améliorer le service (RGPD)
                    </p>
                  </div>
                  <Switch
                    checked={allowTraining}
                    onCheckedChange={handleAllowTrainingChange}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Exporter l'historique du chat</Label>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm">
                      Exporter CSV
                    </Button>
                    <Button variant="outline" size="sm">
                      Exporter JSON
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        );

      case "alerts":
        return (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Alertes personnalisées</CardTitle>
                <CardDescription>
                  Créez et gérez vos alertes pour rester informé
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Activer les alertes personnalisées</Label>
                    <p className="text-sm text-muted-foreground">
                      Recevoir des notifications pour les nouveaux documents
                    </p>
                  </div>
                  <Switch
                    checked={alertsEnabled}
                    onCheckedChange={handleAlertsEnabledChange}
                  />
                </div>
                <Button
                  onClick={handleOpenCreateAlertDialog}
                  disabled={!alertsEnabled}
                >
                  Créer une nouvelle alerte
                </Button>

                <div className="space-y-3">
                  <h4 className="font-medium">Alertes existantes</h4>
                  {!alertsEnabled && customAlerts.length > 0 && (
                    <div className="bg-amber-50 border border-amber-200 rounded-md p-3 mb-3">
                      <p className="text-sm text-amber-800">
                        ⚠️ Les alertes sont actuellement désactivées
                        globalement. Activez le switch ci-dessus pour recevoir
                        des notifications.
                      </p>
                    </div>
                  )}
                  {isLoadingAlerts ? (
                    <div className="text-center py-4">
                      <p className="text-sm text-muted-foreground">
                        Chargement des alertes...
                      </p>
                    </div>
                  ) : customAlerts.length === 0 ? (
                    <div className="text-center py-8 border rounded-lg">
                      <p className="text-sm text-muted-foreground">
                        Aucune alerte créée pour le moment
                      </p>
                      <p className="text-sm text-muted-foreground mt-2">
                        Créez votre première alerte pour être notifié des
                        nouveaux documents
                      </p>
                    </div>
                  ) : (
                    <>
                      <div
                        className={`space-y-3 relative ${
                          !alertsEnabled ? "opacity-50" : ""
                        }`}
                      >
                        {customAlerts.map((alert) => (
                          <div
                            key={alert.id}
                            className="flex items-center justify-between p-3 border rounded-lg"
                          >
                            <div className="flex-1">
                              <h5 className="font-medium">{alert.name}</h5>
                              {alert.keywords && (
                                <p className="text-sm text-muted-foreground">
                                  Mots-clés: {alert.keywords}
                                </p>
                              )}
                              {alert.semanticSearch && (
                                <p className="text-sm text-muted-foreground">
                                  Recherche sémantique: "{alert.semanticSearch}"
                                </p>
                              )}
                              <p className="text-sm text-muted-foreground">
                                {alert.jurisdictions &&
                                  alert.jurisdictions.length > 0 && (
                                    <>
                                      Juridiction:{" "}
                                      {alert.jurisdictions.map((j) => t(getCountryLabelKey(j))).join(", ")} •{" "}
                                    </>
                                  )}
                                {alert.categories &&
                                  alert.categories.length > 0 && (
                                    <>
                                      Catégorie: {alert.categories.map((c) => t(getCategoryLabelKey(c))).join(", ")}
                                    </>
                                  )}
                                {alert.categories &&
                                  alert.categories.length > 0 &&
                                  alert.documentTypes &&
                                  alert.documentTypes.length > 0 && <> • </>}
                                {alert.documentTypes &&
                                  alert.documentTypes.length > 0 && (
                                    <>Type: {alert.documentTypes.map((dt) => t(getTypeLabelKey(dt))).join(", ")}</>
                                  )}
                                {alert.similarityScore && (
                                  <>
                                    {" "}
                                    • Score similarité: {alert.similarityScore}%
                                  </>
                                )}
                              </p>
                            </div>
                            <div className="flex items-center space-x-2">
                              <Switch
                                checked={alert.isActive}
                                onCheckedChange={(checked) =>
                                  handleToggleAlert(alert.id, checked)
                                }
                                disabled={!alertsEnabled}
                              />
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleOpenEditAlertDialog(alert)}
                                disabled={!alertsEnabled}
                              >
                                Modifier
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleOpenDeleteDialog(alert.id)}
                                disabled={!alertsEnabled}
                              >
                                Supprimer
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                      {totalAlerts > alertsPageSize && (
                        <div className="mt-4">
                          <Pagination
                            currentPage={alertsPage}
                            onPageChange={setAlertsPage}
                            itemsPerPage={alertsPageSize}
                            totalItems={totalAlerts}
                            itemName="alertes"
                          />
                        </div>
                      )}
                    </>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Configuration des notifications</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Canal de réception</Label>
                  <p className="text-sm text-muted-foreground mb-3">
                    Choisissez où vous souhaitez recevoir vos notifications
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="channel-email"
                        checked={alertEmail}
                        onCheckedChange={(checked) =>
                          handleNotificationChannelChange(
                            "email",
                            checked as boolean
                          )
                        }
                      />
                      <Label
                        htmlFor="channel-email"
                        className="text-sm font-normal cursor-pointer flex items-center gap-2"
                      >
                        <Mail className="h-4 w-4" />
                        Email - Recevoir les notifications par email
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="channel-inapp"
                        checked={alertInApp}
                        onCheckedChange={(checked) =>
                          handleNotificationChannelChange(
                            "inApp",
                            checked as boolean
                          )
                        }
                      />
                      <Label
                        htmlFor="channel-inapp"
                        className="text-sm font-normal cursor-pointer flex items-center gap-2"
                      >
                        <MessageSquare className="h-4 w-4" />
                        In-app - Voir les notifications dans l'application
                      </Label>
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Fréquences globales</Label>
                  <Select
                    value={alertFrequency}
                    onValueChange={handleAlertFrequencyChange}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="immediate">Immédiat</SelectItem>
                      <SelectItem value="daily">
                        Regroupé (quotidien)
                      </SelectItem>
                      <SelectItem value="weekly">Hebdomadaire</SelectItem>
                      <SelectItem value="monthly">Digest mensuel</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Historique des alertes envoyées</Label>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleOpenHistoryDialog}
                  >
                    Voir l'historique
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        );

      case "privacy":
        return (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Sécurité du compte</CardTitle>
                <CardDescription>
                  Gérez la sécurité de votre compte
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Modifier mot de passe</Label>
                  <div className="space-y-2">
                    <div>
                      <Input
                        type="password"
                        placeholder="Ancien mot de passe"
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        className={
                          passwordErrors.currentPassword ? "border-red-500" : ""
                        }
                      />
                      {passwordErrors.currentPassword && (
                        <p className="text-sm text-red-500 mt-1">
                          {passwordErrors.currentPassword}
                        </p>
                      )}
                    </div>
                    <div>
                      <Input
                        type="password"
                        placeholder="Nouveau mot de passe"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className={
                          passwordErrors.newPassword ? "border-red-500" : ""
                        }
                      />
                      {passwordErrors.newPassword && (
                        <p className="text-sm text-red-500 mt-1">
                          {passwordErrors.newPassword}
                        </p>
                      )}
                    </div>
                    <div>
                      <Input
                        type="password"
                        placeholder="Confirmer le nouveau mot de passe"
                        value={confirmNewPassword}
                        onChange={(e) => setConfirmNewPassword(e.target.value)}
                        className={
                          passwordErrors.confirmNewPassword
                            ? "border-red-500"
                            : ""
                        }
                      />
                      {passwordErrors.confirmNewPassword && (
                        <p className="text-sm text-red-500 mt-1">
                          {passwordErrors.confirmNewPassword}
                        </p>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Le mot de passe doit contenir au moins 8 caractères, une
                      majuscule, une minuscule et un chiffre
                    </p>
                    <Button
                      size="sm"
                      onClick={handleChangePassword}
                      disabled={isChangingPassword}
                    >
                      {isChangingPassword
                        ? "Modification..."
                        : "Modifier le mot de passe"}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            <DataExportManager />

            <Card>
              <CardHeader>
                <CardTitle>Suppression du compte</CardTitle>
                <CardDescription>
                  Action irréversible de suppression de votre compte
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Supprimer mon compte</Label>
                  <p className="text-sm text-muted-foreground">
                    Suppression définitive de votre compte et de toutes vos
                    données
                  </p>
                  <Button variant="destructive">Supprimer le compte</Button>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Sessions actives</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <div>
                      <p className="font-medium">Chrome sur Windows</p>
                      <p className="text-sm text-muted-foreground">
                        IP: 192.168.1.100 • Paris, France
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Dernière activité: Il y a 5 minutes
                      </p>
                    </div>
                    <Badge variant="secondary">Session actuelle</Badge>
                  </div>
                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <div>
                      <p className="font-medium">Safari sur iPhone</p>
                      <p className="text-sm text-muted-foreground">
                        IP: 192.168.1.101 • Paris, France
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Dernière activité: Il y a 2 heures
                      </p>
                    </div>
                    <Button variant="outline" size="sm">
                      Déconnecter
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Vérification d'identité</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Statut de vérification</Label>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">En attente</Badge>
                    <span className="text-sm text-muted-foreground">
                      Documents en cours de vérification
                    </span>
                  </div>
                </div>
                <Button variant="outline">
                  Uploader des pièces d'identité
                </Button>
              </CardContent>
            </Card>
          </div>
        );

      case "subscription":
        return (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Plan actuel</CardTitle>
                <CardDescription>
                  Gérez votre abonnement et vos options de facturation
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium">Plan Professionnel</h4>
                    <p className="text-sm text-muted-foreground">
                      Accès complet à la base documentaire, chatbot IA,
                      certifications d'experts
                    </p>
                  </div>
                  <Badge>Actif</Badge>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Prix mensuel</p>
                    <p className="font-medium">49€/mois HT</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">
                      Date de renouvellement
                    </p>
                    <p className="font-medium">15 février 2025</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">
                      Prochaine facturation
                    </p>
                    <p className="font-medium">15 février 2025</p>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button
                    variant="outline"
                    className="w-full sm:w-auto bg-transparent"
                  >
                    Changer de plan / voir comparatif
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Méthode de paiement</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <div>
                      <p className="font-medium">Carte bancaire •••• 4242</p>
                      <p className="text-sm text-muted-foreground">
                        Expire 12/2027
                      </p>
                    </div>
                    <Button variant="outline" size="sm">
                      Mettre à jour
                    </Button>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm">
                    Ajouter SEPA
                  </Button>
                  <Button variant="outline" size="sm">
                    Facturation d'entreprise
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Facturation</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>
                    Contacts de facturation / adresse de facturation
                  </Label>
                  <div className="space-y-2">
                    <Input
                      placeholder="Nom de l'entreprise"
                      value={companyName}
                      onChange={(e) => handleCompanyNameChange(e.target.value)}
                    />
                    <Input
                      placeholder="Adresse"
                      value={address}
                      onChange={(e) => handleAddressChange(e.target.value)}
                    />
                    <Input
                      placeholder="Numéro de TVA (optionnel)"
                      value={vatNumber}
                      onChange={(e) => handleVatNumberChange(e.target.value)}
                    />
                  </div>
                  <Button variant="outline" size="sm">
                    Mettre à jour
                  </Button>
                </div>
                <Separator />
                <div className="space-y-2">
                  <Label>Factures téléchargeables (PDF)</Label>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between p-2 border rounded">
                      <div>
                        <p className="text-sm font-medium">
                          Janvier 2025 - 49€
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Payée le 15/01/2025
                        </p>
                      </div>
                      <Button variant="outline" size="sm">
                        Télécharger PDF
                      </Button>
                    </div>
                    <div className="flex items-center justify-between p-2 border rounded">
                      <div>
                        <p className="text-sm font-medium">
                          Décembre 2024 - 49€
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Payée le 15/12/2024
                        </p>
                      </div>
                      <Button variant="outline" size="sm">
                        Télécharger PDF
                      </Button>
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Historique des paiements</Label>
                  <Button variant="outline" size="sm">
                    Voir l'historique complet
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        );

      case "members":
        return (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Gestion des membres</CardTitle>
                <CardDescription>
                  Gérez les membres de votre équipe et leurs accès
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Dialog
                  open={isInviteDialogOpen}
                  onOpenChange={setIsInviteDialogOpen}
                >
                  <DialogTrigger asChild>
                    <Button>Inviter un membre</Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-[425px]">
                    <DialogHeader>
                      <DialogTitle>Inviter un nouveau membre</DialogTitle>
                      <DialogDescription>
                        Saisissez les informations du membre que vous souhaitez
                        inviter. Un email d'activation sera envoyé à cette
                        personne.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="grid gap-4 py-4">
                      <div className="space-y-2">
                        <Label htmlFor="email">Email *</Label>
                        <Input
                          id="email"
                          type="email"
                          placeholder="exemple@domaine.com"
                          value={inviteEmail}
                          onChange={(e) => setInviteEmail(e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="firstName">Prénom *</Label>
                        <Input
                          id="firstName"
                          placeholder="Prénom"
                          value={inviteFirstName}
                          onChange={(e) => setInviteFirstName(e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="lastName">Nom *</Label>
                        <Input
                          id="lastName"
                          placeholder="Nom de famille"
                          value={inviteLastName}
                          onChange={(e) => setInviteLastName(e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="role">Rôle</Label>
                        <Select
                          value={inviteRole}
                          onValueChange={setInviteRole}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Sélectionner un rôle" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="MEMBER">Membre</SelectItem>
                            <SelectItem value="MANAGER">Manager</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setIsInviteDialogOpen(false)}
                        disabled={isInviting}
                      >
                        Annuler
                      </Button>
                      <Button
                        type="button"
                        onClick={handleInviteMember}
                        disabled={isInviting}
                      >
                        {isInviting
                          ? "Envoi en cours..."
                          : "Envoyer l'invitation"}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>

                {/* Popup de modification de rôle */}
                <Dialog
                  open={isRoleDialogOpen}
                  onOpenChange={setIsRoleDialogOpen}
                >
                  <DialogContent className="sm:max-w-[425px]">
                    <DialogHeader>
                      <DialogTitle>Modifier le rôle</DialogTitle>
                      <DialogDescription>
                        Modifiez le rôle de {selectedMember?.firstName}{" "}
                        {selectedMember?.lastName}
                      </DialogDescription>
                    </DialogHeader>
                    <div className="grid gap-4 py-4">
                      <div className="space-y-2">
                        <Label>Membre</Label>
                        <div className="p-3 border rounded-lg bg-muted/50">
                          <p className="font-medium">
                            {selectedMember?.firstName}{" "}
                            {selectedMember?.lastName}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {selectedMember?.email}
                          </p>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="newRole">Nouveau rôle</Label>
                        <Select value={newRole} onValueChange={setNewRole}>
                          <SelectTrigger>
                            <SelectValue placeholder="Sélectionner un rôle" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="MEMBER">Membre</SelectItem>
                            <SelectItem value="MANAGER">Manager</SelectItem>
                          </SelectContent>
                        </Select>
                        <div className="text-xs text-muted-foreground mt-1">
                          {newRole === "MEMBER" &&
                            "Accès standard : consultation documents, chatbot IA"}
                          {newRole === "MANAGER" &&
                            "Accès étendu : gestion équipe, rapports"}
                        </div>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setIsRoleDialogOpen(false)}
                        disabled={isUpdatingRole}
                      >
                        Annuler
                      </Button>
                      <Button
                        type="button"
                        onClick={handleUpdateMemberRole}
                        disabled={isUpdatingRole}
                      >
                        {isUpdatingRole
                          ? "Modification..."
                          : "Modifier le rôle"}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>

                {/* Popup de confirmation de révocation */}
                <Dialog
                  open={isRevokeDialogOpen}
                  onOpenChange={setIsRevokeDialogOpen}
                >
                  <DialogContent className="sm:max-w-[425px]">
                    <DialogHeader>
                      <DialogTitle className="flex items-center gap-2 text-red-600">
                        <Shield className="h-5 w-5" />
                        Confirmer la révocation
                      </DialogTitle>
                      <DialogDescription>
                        Cette action est irréversible. L'utilisateur perdra
                        immédiatement l'accès à la plateforme et devra être
                        invité à nouveau pour y accéder.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="py-4">
                      <div className="p-4 border border-red-200 rounded-lg bg-red-50 dark:bg-red-950/20 dark:border-red-800">
                        <p className="font-medium text-red-900 dark:text-red-100">
                          Utilisateur à révoquer :
                        </p>
                        <p className="text-red-700 dark:text-red-200 mt-1">
                          {memberToRevoke?.firstName} {memberToRevoke?.lastName}
                        </p>
                        <p className="text-sm text-red-600 dark:text-red-300">
                          {memberToRevoke?.email}
                        </p>
                      </div>
                      <p className="text-sm text-muted-foreground mt-3">
                        Êtes-vous sûr de vouloir révoquer l'accès de cet
                        utilisateur ?
                      </p>
                    </div>
                    <DialogFooter>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setIsRevokeDialogOpen(false);
                          setMemberToRevoke(null);
                        }}
                        disabled={
                          !!memberToRevoke &&
                          revokingUserIds.includes(memberToRevoke.id)
                        }
                      >
                        Annuler
                      </Button>
                      <Button
                        type="button"
                        variant="destructive"
                        onClick={handleRevokeUser}
                        disabled={
                          !!memberToRevoke &&
                          revokingUserIds.includes(memberToRevoke.id)
                        }
                      >
                        {!!memberToRevoke &&
                        revokingUserIds.includes(memberToRevoke.id) ? (
                          <>
                            <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2"></div>
                            Révocation...
                          </>
                        ) : (
                          "Confirmer la révocation"
                        )}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>

                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium">Liste des membres</h4>
                    {!isLoadingMembers && totalMembers > 0 && (
                      <span className="text-sm text-muted-foreground">
                        {totalMembers} membre{totalMembers > 1 ? "s" : ""} au
                        total
                      </span>
                    )}
                  </div>
                  {isLoadingMembers ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="text-center">
                        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                        <p className="text-sm text-muted-foreground">
                          Chargement des membres...
                        </p>
                      </div>
                    </div>
                  ) : members.length === 0 ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="text-center">
                        <Users className="h-12 w-12 text-muted-foreground mb-4 mx-auto" />
                        <h3 className="text-lg font-semibold mb-2">
                          Aucun membre
                        </h3>
                        <p className="text-muted-foreground">
                          Invitez des membres pour commencer à collaborer.
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {members.map((member) => (
                        <div
                          key={member.id}
                          className="flex items-center justify-between p-3 border rounded-lg"
                        >
                          <div>
                            <p className="font-medium">
                              {member.title} {member.firstName}{" "}
                              {member.lastName}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {member.email}
                            </p>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge
                                variant={
                                  member.roles.includes("MANAGER")
                                    ? "secondary"
                                    : "default"
                                }
                              >
                                {member.roles.includes("MANAGER")
                                  ? "Manager"
                                  : "Membre"}
                              </Badge>
                              <Badge variant={"outline"}>
                                {member.status === "ACTIVATED"
                                  ? "Actif"
                                  : "Invitation envoyée"}
                              </Badge>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleOpenRoleDialog(member)}
                            >
                              Modifier rôle
                            </Button>

                            {/* Bouton Renvoyer invitation - affiché seulement si pas encore activé */}
                            {member.status !== "ACTIVATED" && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                  handleResendInvitation(
                                    member.id,
                                    member.email
                                  )
                                }
                                disabled={resendingInvitationIds.includes(
                                  member.id
                                )}
                              >
                                {resendingInvitationIds.includes(member.id) ? (
                                  <>
                                    <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-1"></div>
                                    Envoi...
                                  </>
                                ) : (
                                  "Renvoyer invitation"
                                )}
                              </Button>
                            )}

                            {/* Bouton Révoquer - toujours affiché */}
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => handleOpenRevokeDialog(member)}
                              disabled={revokingUserIds.includes(member.id)}
                            >
                              {revokingUserIds.includes(member.id) ? (
                                <>
                                  <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-1"></div>
                                  Révocation...
                                </>
                              ) : (
                                "Révoquer"
                              )}
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Pagination */}
                  {!isLoadingMembers &&
                    members.length > 0 &&
                    totalMembers > membersPageSize && (
                      <Pagination
                        currentPage={membersPage}
                        totalItems={totalMembers}
                        itemsPerPage={membersPageSize}
                        onPageChange={setMembersPage}
                        itemName="membres"
                        className="mt-6"
                      />
                    )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Rôles et permissions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="p-3 border rounded-lg">
                    <h5 className="font-medium">Manager</h5>
                    <p className="text-sm text-muted-foreground">
                      Accès complet : gestion des membres, facturation,
                      paramètres d'équipe
                    </p>
                  </div>
                  <div className="p-3 border rounded-lg">
                    <h5 className="font-medium">Utilisateur</h5>
                    <p className="text-sm text-muted-foreground">
                      Accès standard : consultation documents, chatbot IA,
                      demandes de certification
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        );

      default:
        return null;
    }
  };

  const activeTabIcon = settingsTabs.find((tab) => tab.id === activeTab)?.icon;
  const activeTabLabel = settingsTabs.find(
    (tab) => tab.id === activeTab
  )?.label;

  return (
    <div className="h-full w-full bg-background">
      {/* Dialogue de création/modification d'alerte */}
      <Dialog open={isAlertDialogOpen} onOpenChange={setIsAlertDialogOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingAlert ? "Modifier l'alerte" : "Créer une nouvelle alerte"}
            </DialogTitle>
            <DialogDescription>
              Configurez votre alerte personnalisée pour être notifié des
              nouveaux documents correspondants
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="alertName">Nom de l'alerte (optionnel)</Label>
              <Input
                id="alertName"
                placeholder="Ex: Droit du travail - Télétravail (généré automatiquement si vide)"
                value={alertName}
                onChange={(e) => setAlertName(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Si vous ne renseignez pas de nom, il sera généré automatiquement
                selon vos critères
              </p>
            </div>

            <Separator />

            <div className="space-y-2">
              <Label htmlFor="alertKeywords">Mots-clés</Label>
              <Input
                id="alertKeywords"
                placeholder="Ex: télétravail, travail à distance"
                value={alertKeywords}
                onChange={(e) => setAlertKeywords(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Séparez les mots-clés par des virgules
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="alertSemanticSearch">Recherche sémantique</Label>
              <Input
                id="alertSemanticSearch"
                placeholder="Ex: protection des données personnelles"
                value={alertSemanticSearch}
                onChange={(e) => setAlertSemanticSearch(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Décrivez le concept recherché en langage naturel
              </p>
            </div>

            {alertSemanticSearch && (
              <div className="space-y-2">
                <Label htmlFor="alertSimilarityScore">
                  Score de similarité minimum: {alertSimilarityScore}%
                </Label>
                <input
                  id="alertSimilarityScore"
                  type="range"
                  min="50"
                  max="100"
                  value={alertSimilarityScore}
                  onChange={(e) =>
                    setAlertSimilarityScore(Number(e.target.value))
                  }
                  className="w-full"
                />
                <p className="text-xs text-muted-foreground">
                  Plus le score est élevé, plus les résultats seront précis
                </p>
              </div>
            )}

            <Separator />

            <div className="space-y-3">
              <Label>Juridictions</Label>
              <div className="grid grid-cols-2 gap-3 border rounded-md p-3">
                {jurisdictions.map((jurisdiction) => (
                  <div
                    key={jurisdiction}
                    className="flex items-center space-x-2"
                  >
                    <Checkbox
                      id={`alert-jurisdiction-${jurisdiction}`}
                      checked={alertJurisdictions.includes(jurisdiction)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setAlertJurisdictions([
                            ...alertJurisdictions,
                            jurisdiction,
                          ]);
                        } else {
                          setAlertJurisdictions(
                            alertJurisdictions.filter((j) => j !== jurisdiction)
                          );
                        }
                      }}
                    />
                    <label
                      htmlFor={`alert-jurisdiction-${jurisdiction}`}
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                    >
                      {t(getCountryLabelKey(jurisdiction))}
                    </label>
                  </div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                Sélectionnez une ou plusieurs juridictions (optionnel)
              </p>
            </div>

            <div className="space-y-3">
              <Label>Catégories</Label>
              <div className="grid grid-cols-2 gap-3 max-h-48 overflow-y-auto border rounded-md p-3">
                {categories.map((category) => (
                  <div key={category.value} className="flex items-center space-x-2">
                    <Checkbox
                      id={`alert-category-${category.value}`}
                      checked={alertCategories.includes(category.value)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setAlertCategories([...alertCategories, category.value]);
                        } else {
                          setAlertCategories(
                            alertCategories.filter((c) => c !== category.value)
                          );
                        }
                      }}
                    />
                    <label
                      htmlFor={`alert-category-${category.value}`}
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                    >
                      {t(category.labelKey)}
                    </label>
                  </div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                Sélectionnez une ou plusieurs catégories (optionnel)
              </p>
            </div>

            <div className="space-y-3">
              <Label>Types de documents</Label>
              <div className="grid grid-cols-2 gap-3 max-h-48 overflow-y-auto border rounded-md p-3">
                {documentTypes.map((type) => (
                  <div key={type.value} className="flex items-center space-x-2">
                    <Checkbox
                      id={`alert-type-${type.value}`}
                      checked={alertDocumentTypes.includes(type.value)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setAlertDocumentTypes([...alertDocumentTypes, type.value]);
                        } else {
                          setAlertDocumentTypes(
                            alertDocumentTypes.filter((t) => t !== type.value)
                          );
                        }
                      }}
                    />
                    <label
                      htmlFor={`alert-type-${type.value}`}
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                    >
                      {t(type.labelKey)}
                    </label>
                  </div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                Sélectionnez un ou plusieurs types (optionnel)
              </p>
            </div>

            <Separator />

            <div className="flex items-center space-x-2">
              <Switch
                id="alertIsActive"
                checked={alertIsActive}
                onCheckedChange={setAlertIsActive}
              />
              <Label htmlFor="alertIsActive">
                Activer cette alerte immédiatement
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setIsAlertDialogOpen(false);
                resetAlertForm();
              }}
              disabled={isCreatingAlert}
            >
              Annuler
            </Button>
            <Button
              type="button"
              onClick={handleSaveAlert}
              disabled={isCreatingAlert}
            >
              {isCreatingAlert
                ? "Sauvegarde..."
                : editingAlert
                ? "Modifier l'alerte"
                : "Créer l'alerte"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de confirmation de suppression */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmer la suppression</DialogTitle>
            <DialogDescription>
              Êtes-vous sûr de vouloir supprimer cette alerte ? Cette action est
              irréversible.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={handleCancelDelete}>
              Annuler
            </Button>
            <Button variant="destructive" onClick={handleConfirmDeleteAlert}>
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog d'historique des notifications d'alertes */}
      <Dialog
        open={isHistoryDialogOpen}
        onOpenChange={setIsHistoryDialogOpen}
      >
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Historique des alertes envoyées</DialogTitle>
            <DialogDescription>
              Les notifications reçues pour vos alertes personnalisées
            </DialogDescription>
          </DialogHeader>
          {isLoadingHistory ? (
            <div className="text-center py-8">
              <p className="text-sm text-muted-foreground">
                Chargement de l'historique...
              </p>
            </div>
          ) : notificationHistory.length === 0 ? (
            <div className="text-center py-8 border rounded-lg">
              <p className="text-sm text-muted-foreground">
                Aucune notification reçue pour le moment
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                Vous verrez ici les notifications envoyées lorsque de nouveaux
                documents correspondront à vos alertes
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {notificationHistory.map((entry) => (
                <div
                  key={entry.id}
                  className="p-3 border rounded-lg space-y-2"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <Bell className="h-4 w-4 shrink-0 text-muted-foreground" />
                      <h5 className="font-medium truncate">
                        {entry.customAlertName}
                      </h5>
                      <Badge variant="secondary">
                        {notificationTypeLabels[entry.notificationType] ||
                          entry.notificationType}
                      </Badge>
                    </div>
                    <span className="text-xs text-muted-foreground whitespace-nowrap">
                      {formatDateTime(entry.sentAt)}
                    </span>
                  </div>
                  {entry.documents && entry.documents.length > 0 ? (
                    <ul className="space-y-1 pl-6">
                      {entry.documents.map((doc) => (
                        <li
                          key={doc.id}
                          className="flex items-center gap-2 min-w-0"
                        >
                          <button
                            type="button"
                            onClick={() => handleOpenHistoryDocument(doc.id)}
                            className="text-sm text-primary hover:underline text-left flex items-center gap-1 min-w-0"
                          >
                            <FileText className="h-3.5 w-3.5 shrink-0" />
                            <span className="truncate">{doc.title}</span>
                          </button>
                          {(doc.category || doc.type) && (
                            <span className="text-xs text-muted-foreground whitespace-nowrap">
                              {[
                                doc.category
                                  ? t(getCategoryLabelKey(doc.category))
                                  : null,
                                doc.type ? t(getTypeLabelKey(doc.type)) : null,
                              ]
                                .filter(Boolean)
                                .join(" • ")}
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-muted-foreground pl-6">
                      {entry.documentCount} document(s)
                    </p>
                  )}
                </div>
              ))}
              {totalHistory > historyPageSize && (
                <Pagination
                  currentPage={historyPage}
                  onPageChange={setHistoryPage}
                  itemsPerPage={historyPageSize}
                  totalItems={totalHistory}
                  itemName="notifications"
                />
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <div className="py-4 px-6 sm:px-12 sm:py-8 bg-background">
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold">Paramètres</h1>
          <p className="text-muted-foreground">
            Gérez vos préférences et paramètres de compte
          </p>
        </div>

        <div className="lg:hidden mb-4">
          <Button
            variant="outline"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="w-full justify-between"
          >
            <span className="flex items-center gap-2">
              {activeTabIcon &&
                React.createElement(activeTabIcon, { className: "h-4 w-4" })}
              {activeTabLabel}
            </span>
            {isMobileMenuOpen ? (
              <X className="h-4 w-4" />
            ) : (
              <Menu className="h-4 w-4" />
            )}
          </Button>
        </div>

        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
          <div
            className={`
            ${isMobileMenuOpen ? "block" : "hidden"} lg:block
            lg:w-64 space-y-1
            ${
              isMobileMenuOpen
                ? "fixed inset-0 z-50 bg-background p-4 lg:relative lg:inset-auto lg:z-auto lg:bg-transparent lg:p-0"
                : ""
            }
          `}
          >
            {isMobileMenuOpen && (
              <div className="flex justify-between items-center mb-4 lg:hidden">
                <h2 className="text-lg font-semibold">Menu</h2>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            )}
            {settingsTabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => {
                    setActiveTab(tab.id);
                    setIsMobileMenuOpen(false);
                  }}
                  className={`w-full flex items-center space-x-3 px-3 py-2 text-left rounded-lg transition-colors ${
                    activeTab === tab.id
                      ? "bg-primary text-primary-foreground"
                      : "hover:bg-accent hover:text-accent-foreground"
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>

          <div className="flex-1 min-w-0">{renderTabContent()}</div>
        </div>
      </div>
    </div>
  );
}
